<template>
	<div id="app">
		<router-view />
		<div class="popups" v-if="popupVisible && selectedPopup">
			<ContainerObject :containerObject="selectedPopup" />
		</div>
		<LoadingComponent v-if="loading" />
	</div>
</template>
<script lang="ts">
import ContainerObject from "@/components/layout/ContainerObject.vue";
import LoadingComponent from "@/components/utils/LoadingComponent.vue";
import { Campaign } from "@/types/campaign";
import { parse } from "tldts";
import router from "./router/index";
import { gtmService } from "@/utils/gtm";
import { buildCampaignFontCss } from "@/utils/campaignFontCss";


import { ActionType, Container } from "./types/customPage";
import { v4 as uuidv4 } from "uuid";

import { defineComponent } from "vue";
import { customPageStore } from "./store/pinia_modules/customPageStore";
import { vItemStore } from "./store/pinia_modules/vItemStore";
import { dropPoolStore } from "./store/pinia_modules/dropPoolStore";
import { poolSegmentStore } from "./store/pinia_modules/poolSegmentStore";
import { navigationStore } from "./store/pinia_modules/navigationStore";
import { authStore } from "./store/pinia_modules/authStore";
import { campaignStore } from "./store/pinia_modules/campaignStore";
import { customComponentStore } from "./store/pinia_modules/customComponentStore";
import {
	PendingEventType,
	pendingEventStore,
} from "./store/pinia_modules/pendingEventStore";
import { categoryStore } from "./store/pinia_modules/categoryStore";
import { prizeStore } from "./store/pinia_modules/prizeStore";
import moment from "moment";
import { deepLinkStore } from "./store/pinia_modules/deepLinkStore";
import { experienceStore } from "./store/pinia_modules/experienceStore";
import { ProjectError } from "./errors";
import { derivePageObservability, OBSERVABILITY_EVENT_NAMES } from "./utils/observability";

export default defineComponent({
	components: {
		ContainerObject,
		LoadingComponent,
	},
	data() {
		return {
			subDomain: null as string | null,
			campaignId: null as string | null,
			path: null as string | null,
			email: null as string | null,
			otc: null as string | null,
			shareCode: null as string | null,
			shareRewardCode: null as string | null,
			errorMessage: "",
			shortMessage: "",
			campaignEnded: false,
			campaignNotStarted: false,
			notForDesktop: false,
		};
	},
	mounted() {
		// Initialize GTM early in the app lifecycle
		this.initializeGTM();
		this.init();
	},
		methods: {
			isErrorRoute(): boolean {
				return window.location.pathname === "/error";
			},
			isEmbeddedSrcdocContext(): boolean {
				const href = window.location.href || "";
				const pathname = window.location.pathname || "";
				return href.startsWith("about:srcdoc") || pathname === "/srcdoc" || pathname === "srcdoc";
			},
			isUuid(value: string): boolean {
				return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
					value
				);
			},
		async initializeGTM() {
			try {
				await gtmService.initialize();
			} catch (error) {
				console.warn("GTM initialization failed:", error);
			}
		},
			async init() {
				try {
					if (this.isErrorRoute()) {
						this.getQueryParams();
						navigationStore().loading = false;
						return;
					}

					if (this.isEmbeddedSrcdocContext()) {
						console.info("App.vue init: skipping SPA bootstrap inside srcdoc context");
						navigationStore().loading = false;
						return;
					}

					navigationStore().loading = true;

				// Default to transparent loading for normal navigation
				navigationStore().setLoadingTransparent(true);

				//Clear local storage
				customPageStore().clearItems();
				vItemStore().clearItems();
				dropPoolStore().clearItems();
				poolSegmentStore().clearItems();
				deepLinkStore().clear();
				experienceStore().reset();

				//Create user UUID if it doesn't exist
				this.createUserUUID();

				//Get all required variables
				this.getSubdomain();
				await this.getPath();

				let campaignIdentifier: {
					subdomain: string | null;
					campaignId: string | null;
				} = { subdomain: null, campaignId: null };

				if (this.campaignId) {
					campaignIdentifier.campaignId = this.campaignId;
				} else if (this.subDomain) {
					campaignIdentifier.subdomain = this.subDomain;
				}

				const uiElementsLoaded = await this.getUIElements(campaignIdentifier);

				if (this.campaign && this.campaign.paused) {
					navigationStore().loading = false;
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "campaign-paused",
					});
					return;
				}

				if (this.campaignNotStarted) {
					navigationStore().loading = false;
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "campaign-paused",
					});
					return;
				}

				if (this.campaignEnded) {
					navigationStore().loading = false;
					navigationStore().emitAction({
						type: ActionType.GO_TO_PAGE,
						value: "campaign-over",
					});

					return;
				}

				if (!uiElementsLoaded) {
					navigationStore().loading = false;
					return;
				}

				this.getQueryParams();

				//Submit user UUID if campaign ID is valid
				this.submitUserUUID();

				// Only redirect to campaign root if we're not already on a specific page
				if (this.checkCurrentPath()) {
					console.log("App.vue init: On root path - redirecting to campaign home");
					router.push(`/${this.campaignId}`);
				} else {
					console.log("App.vue init: On specific page - not redirecting");
				}

				await Promise.all([
					this.checkIfShareCodeExists(),
					this.checkIfShareRewardCodeExists(),
					this.checkAutoLogin(),
					this.checkAutoReg(),
				]);

				this.startPendingEvents();
				await this.checkAuthenticated();

				// get vitems in wallet
				await this.checkIfDeepLink();
				await vItemStore().getItems();
				await this.checkIfVitemsArePendingAndDeepLink();
				experienceStore().resolveFromState({
					routeName: router.currentRoute.value.name?.toString() || null,
					pools: dropPoolStore().getPools,
					pendingItems: vItemStore().getPendingItems,
				});

				navigationStore().loading = false;
			} catch (err) {
				console.error(err);
				if (err instanceof Error) {
					this.errorMessage = err.message;
					this.shortMessage = "An application error has occurred";
					return this.showErrorPage();
				} else {
					this.errorMessage = "Unknown error";
					this.shortMessage = "An application error has occurred";
					return this.showErrorPage();
				}
			}
		},

		createUserUUID() {
			console.log(this.userUUID);
			authStore().ensureCorrelationIds();
			if (!this.userUUID) {
				const uuid = uuidv4();
				console.log(uuid);
				authStore().saveUserUUID(uuid);
			}
		},
		submitUserUUID() {
			if (!this.campaignId) {
				console.warn("Skipping landing-page-view because campaignId is not resolved yet");
				return;
			}
			let uuid = "";
			if (this.userUUID) {
				uuid = this.userUUID;
			}
			authStore().dispatchUserEvent({
				campaignId: this.campaignId!,
				eventName: OBSERVABILITY_EVENT_NAMES.navigationLandingViewed,
				event: "landing-page-view",
				identifier: uuid,
				pageRoute: window.location.pathname,
				...derivePageObservability({
					name: typeof router.currentRoute.value.name === "string" ? router.currentRoute.value.name : null,
					path: window.location.pathname,
				}),
				sessionId: authStore().getSessionID || undefined,
				journeyId: authStore().getJourneyID || undefined,
			});
		},
		showErrorPage() {
			navigationStore().loading = false;
			const campaignId =
				this.campaignId || authStore().getCampaignID || campaignStore().getItem?.id || "";
			const params = new URLSearchParams({
				message: this.errorMessage,
				shortMessage: this.shortMessage,
			});
			if (campaignId) {
				params.set("campaign", campaignId);
			}
			router.push(`/error?${params.toString()}`);
		},
		getSubdomain() {
			this.subDomain = parse(window.location.hostname).subdomain;
		},
		async getPath() {
			this.path = window.location.pathname;

			if (this.path[0] == "/") {
				this.path = this.path.substring(1);
			}
			if (this.path[this.path.length - 1] == "/") {
				this.path = this.path.slice(0, -1);
			}
			const split = this.path.split("/");
			if (
				split.length == 1 &&
				split[0] != "" &&
				split[0] != "error" &&
				split[0] != "autoreg" &&
				split[0] != "enter-pin" &&
				split[0] != "autologin"
			) {
				console.log(split[0]);
				if (this.isUuid(split[0])) {
					this.campaignId = split[0];
				} else {
					const resp = await campaignStore().getCampaignFromPath(split[0]);
					if (resp.success) {
						this.campaignId = resp.data.id;
					} else {
						this.campaignId = split[0];
					}
				}
				authStore().saveCampaignID(this.campaignId!);
			}
			if (split.length == 2) {
				this.path = split[1];
				this.campaignId = split[0];
				authStore().saveCampaignID(this.campaignId);
			}
			if (split.length >= 3) {
				// Handle routes like /{campaign}/prize/{vitemID} or /{campaign}/any-page/{param}
				this.campaignId = split[0];
				this.path = split.slice(1).join('/'); // Keep the full sub-path
				authStore().saveCampaignID(this.campaignId);
				console.log("App.vue: Multi-segment route detected - Campaign ID:", this.campaignId, "Path:", this.path);
			}
		},
		getQueryParams() {
			const params = new URL(document.location.toString()).searchParams;
			params.forEach((v, k) => {
				params.set(k, encodeURI(v));
			});
			if (params.get("email")) {
				this.email = params.get("email");
				authStore().saveEmail(
					decodeURIComponent(params.get("email") as string)
				);
			}
			if (params.get("otc")) {
				this.otc = params.get("otc");
				authStore().saveOTC(this.otc!);
			}
			if (params.get("shareCode")) {
				this.shareCode = params.get("shareCode");
			}
			if (params.get("shareRewardCode")) {
				this.shareRewardCode = params.get("shareRewardCode");
			}
			if (params.get("campaign")) {
				this.campaignId = params.get("campaign");
			}
			if (params.get("token") && params.get("walletId") && params.get("userId")) {
				localStorage.setItem("AccessToken", params.get("token")!);
				authStore().saveWalletID(params.get("walletId")!);
				authStore().saveUserID(params.get("userId")!);
			}
			if (params.get("refreshToken")) {
				localStorage.setItem("RefreshToken", params.get("refreshToken")!);
			}
			if (params.get("walletId")) {
				authStore().saveWalletID(params.get("walletId")!);
			}
			if (params.get("userId")) {
				authStore().saveUserID(params.get("userId")!);
			}
			if (params.get("isDeepLink") && params.get("deepLinkId")) {
				try {
					deepLinkStore().start(params.get("deepLinkId")!);
				} catch (error) {
					console.error(new ProjectError("Error fetching deep link", error));
				}
			}
		},
		async checkIfDeepLink() {
			const params = new URL(document.location.toString()).searchParams;
			if (params.get("isDeepLink") && params.get("deepLinkId")) {
				try {
					// Set loading to solid black for deeplinks
					navigationStore().setLoadingTransparent(false);

					await deepLinkStore().start(params.get("deepLinkId")!);
				} catch (error) {
					console.error(new ProjectError("Error fetching deep link", error));
				}
			}
		},
		async checkIfVitemsArePendingAndDeepLink() {
			if (deepLinkStore().isDeepLink && vItemStore().getPendingItems.length > 0) {
				const deepLinkItem = vItemStore().getPendingItems.find(item => item.isDeepLink);
				if (deepLinkItem) {
					const campaign = campaignStore().getItem;
					if (campaign?.deeplinkFirstPage) {
						router.push(`/${this.campaignId}/${campaign.deeplinkFirstPage}`);
					} else {
						router.push(`/${this.campaignId}/ar-viewer`);
					}
				} else {
					console.log("No deep link item found on start up");
				}
			}
		},
		async getUIElements(campaignIdentifier: {
			subdomain: string | null;
			campaignId: string | null;
		}): Promise<boolean> {
			let initialCampaignData;
			try {
				initialCampaignData = await campaignStore().getInitialCampaignData(
					campaignIdentifier
				);
			} catch (err) {
				console.error(err);
				this.errorMessage = "Web App not found";
				this.shortMessage = "Web App not found";
				return false;
			}

			if (!initialCampaignData) {
				navigationStore().loading = false;
				return false;
			}

			this.campaignId = initialCampaignData.campaign.id;
			campaignStore().setCampaign(initialCampaignData.campaign);
			customPageStore().saveItems(initialCampaignData.customPages);
			customComponentStore().saveItems(initialCampaignData.customComponents);
			categoryStore().addItems(initialCampaignData.categories);
			dropPoolStore().saveItems(initialCampaignData.dropPools);
			prizeStore().saveItems(initialCampaignData.prizes);

			const campaign = campaignStore().getItem;
			if (campaign == null) {
				this.errorMessage = "Web App not found";
				this.shortMessage = "Web App not found";
				return false;
			} else {
				authStore().saveCampaignID(campaign.id!);
				if (campaign.pageTitle && campaign.pageTitle != "") {
					document.title = campaign.pageTitle;
					this.setMeta(campaign);
				} else {
					document.title = "Inner Reality";
					this.setMeta(campaign);
				}
				if (campaign.favicon) {
					this.setFavicons(campaign.favicon);
				}
			}
			const today = moment().format("YYYY-MM-DD");
			if (campaign.startDate && moment().isBefore(moment(campaign.startDate))) {
				this.campaignNotStarted = true;
				return false;
			}
			if (moment(campaign.endDate).isBefore(moment(today))) {
				this.campaignEnded = true;
				return false;
			}
			this.addCustomFont();
			return true;
		},
		setMeta(campaign: Campaign) {
			const siteName = document.createElement("meta");
			siteName.setAttribute("property", "og:site_name");
			siteName.setAttribute("content", campaign.pageTitle!);
			const siteTitle = document.createElement("meta");
			siteTitle.setAttribute("property", "og:title");
			siteTitle.setAttribute("content", campaign.pageTitle!);
			const siteDescription = document.createElement("meta");
			siteDescription.setAttribute("property", "og:description");
			siteDescription.setAttribute("content", campaign.description!);
			const siteImage = document.createElement("meta");
			siteImage.setAttribute("property", "og:image:secure_url");
			siteImage.setAttribute("itemprop", "image");
			siteImage.setAttribute("content", campaign.logo!);
			const type = document.createElement("meta");
			type.setAttribute("property", "og:type");
			type.setAttribute("content", "website");
			const time = document.createElement("meta");
			time.setAttribute("property", "og:updated_time");
			time.setAttribute("content", `${Date.now()}`);
			document.head.appendChild(siteName);
			document.head.appendChild(siteTitle);
			document.head.appendChild(siteDescription);
			document.head.appendChild(siteImage);
			document.head.appendChild(type);
			document.head.appendChild(time);
		},
		setFavicons(favImg: string) {
			// const faviconLink = document.createElement("link");
			// faviconLink.rel = "shortcut icon";
			// faviconLink.type = "image/x-icon";
			// faviconLink.href = favImg;
			// document.head.appendChild(faviconLink);

			let newFaviconLink = document.createElement("link");
			newFaviconLink.rel = "icon";
			newFaviconLink.href = favImg;

			var existingFavicon = document.querySelector('link[rel="icon"]');
			if (existingFavicon) {
				existingFavicon.parentNode!.removeChild(existingFavicon);
			}

			document.head.appendChild(newFaviconLink);
		},
		// async checkForDesktop() {
		// 	if (!this.isMobile()) {
		// 		router.push(`/${this.campaignId}/not-for-desktop`);
		// 	} else {
		// 		router.push(`/${this.campaignId}`);
		// 	}
		// },
		async checkIfShareCodeExists(): Promise<void> {
			if (this.shareCode) {
				// Add to pendingEventQueue
				await pendingEventStore().addEvent({
					event: PendingEventType.COLLECT_SHARE,
					value: this.shareCode,
					id: uuidv4(),
				});
				window.location.search = "";
			}
		},
		async checkIfShareRewardCodeExists(): Promise<void> {
			if (this.shareRewardCode) {
				// Add to pendingEventQueue
				await pendingEventStore().addEvent({
					event: PendingEventType.COLLECT_SHARE_REWARD,
					value: this.shareRewardCode,
					id: uuidv4(),
				});
				window.location.search = "";
			}
		},
		async checkAutoLogin() {
			console.log("this.path", this.path);
			if (this.path == "enter-pin" && this.otc && this.email) {
				try {
					this.loading = true;
					const result = await authStore().login(parseInt(this.otc!));
					this.loading = false;
					if (result) {
						const campaign = campaignStore().getItem;
						let pageAfterAuth = campaign?.pageAfterAuth;
						if (!pageAfterAuth || pageAfterAuth == "") {
							pageAfterAuth = "vault";
						}

						// Only redirect if we're on the campaign root, not on specific pages
						if (this.checkCurrentPath()) {
							console.log("checkAutoLogin: On root path - redirecting to:", pageAfterAuth);
							router.push(`/${this.campaignId}/${pageAfterAuth}`);
						} else {
							console.log("checkAutoLogin: On specific page - not redirecting");
						}
					} else {
						// Custom-route pages (T&Cs, how-to-play, campaign-paused,
						// out-of-plays, etc.) must be reachable pre-auth — e.g.
						// users need to read T&Cs before agreeing at registration.
						// Authenticated views (Wallet/Map/AR/Prize) aren't in this
						// allowlist; their own data fetches still require a token.
						if (
							router.currentRoute.value.name !== "Register" &&
							router.currentRoute.value.name !== "SignIn" &&
							router.currentRoute.value.name !== "Enter Pin" &&
							router.currentRoute.value.name !== "Custom"
						) {
							if (router.currentRoute.value.name != undefined) {
								router.push(`/${this.campaignId}`);
							}
						}
					}
				} catch (error) {
					console.error(error);
				}
			}
			if (localStorage.getItem("AccessToken")) {
				const campaign = campaignStore().getItem;
				let pageAfterAuth = campaign?.pageAfterAuth;
				if (!pageAfterAuth || pageAfterAuth == "") {
					pageAfterAuth = "vault";
				}

				// Only redirect if we're on the campaign root, not on specific pages
				if (this.checkCurrentPath()) {
					console.log("checkAutoLogin: On root path - redirecting to:", pageAfterAuth);
					router.push(`/${this.campaignId}/${pageAfterAuth}`);
				} else {
					console.log("checkAutoLogin: On specific page - not redirecting");
				}
			}
		},
		async checkAutoReg() {
			if (this.path == "autoreg") {
				const params = new URL(document.location.toString()).searchParams;
				params.forEach((v, k) => {
					params.set(k, encodeURI(v));
				});
				await authStore().autoRegister({
					queryString: params.toString(),
					email: this.email!,
				});
			}
		},
		checkCurrentPath() {
			const currentPath = window.location.pathname;
			const campaignRootPath = `/${this.campaignId}`;
			if (currentPath === '/' || currentPath === campaignRootPath) {
				return true;
			}
			return false;
		},
		startPendingEvents() {
			if (this.checkTokens()) {
				pendingEventStore().startPendingEvents();
			}
		},
		async checkAuthenticated(): Promise<boolean> {
			return await authStore().checkAuthenticated();
		},
		checkTokens(): boolean {
			if (
				localStorage.getItem("AccessToken") &&
				localStorage.getItem("RefreshToken")
			) {
				return true;
			}
			return false;
		},

		isMobile(): boolean {
			let isMobile =
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				)
					? true
					: false;
			const isDevMode = process.env.NODE_ENV == "development" ? true : false;
			if (isDevMode) {
				isMobile = true;
			}

			return isMobile;
		},

		addCustomFont(): void {
			// IRL2-2864: canonical font system. buildCampaignFontCss accepts both
			// the legacy CSS-rule shape (prodc ask-africa-survey) and the new
			// bare-name shape (campaigns minted post-fix). Ask-africa values pass
			// through byte-for-byte; bare names get a @import URL built; null/
			// empty falls back to the system stack — see campaignFontCss.ts.
			const { importRule, fontFamilyRule } = buildCampaignFontCss(
				this.campaign!.googleFont,
				this.campaign!.fontClass,
			);
			const css = `${importRule}
		html { ${fontFamilyRule} }
		input { ${fontFamilyRule} }
		button { ${fontFamilyRule} }
		`;
			const head = document.head || document.getElementsByTagName("head")[0];
			const style = document.createElement("style");
			head.appendChild(style);
			style.appendChild(document.createTextNode(css));
		},
	},
	computed: {
		campaign(): Campaign | null {
			return campaignStore().getItem;
		},
		popupVisible(): boolean {
			return navigationStore().getPopupVisible;
		},
		selectedPopup(): Container {
			return navigationStore().getSelectedPopup;
		},
		loading(): boolean {
			return navigationStore().getLoading;
		},
		userUUID(): string | null {
			return authStore().getUserUUID;
		},
	},
});
</script>

<style lang="scss">
html {
	box-sizing: border-box;
	height: 100%;
	width: 100%;
	overflow: hidden;
	// font-family: Arial, Helvetica, sans-serif;
}

*,
*:before,
*:after {
	box-sizing: inherit;
}

body {
	height: 100%;
	width: 100%;
	margin: 0;
	padding: 0;
	overflow: hidden;
}

#app {
	// display: block;
	overflow: hidden;
	width: 100%;
	height: 100%;
}

.popups {
	position: fixed;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	z-index: 10000;
}

#splash {
	z-index: -1 !important;
	display: none !important;
}
</style>
